import { useState, useEffect, useRef } from 'react';
import { useColorSchemeInfo } from 'BMapsRedux/prefs/access';

/**
 * SidePanelResizeHandle — drag-resize handle for a SidePanel column.
 *
 * Mirrors the pattern in `proto/src/ui/tables/CompoundTableSidePanel.jsx`
 * (handle on right edge of a left-mounted panel; drag right = wider).
 * This component is direction-agnostic via the `side` prop:
 *
 *   side='left'   handle is on the LEFT edge of a RIGHT-mounted panel.
 *                 Dragging the cursor LEFTWARD widens the panel.
 *                 Width formula: window.innerWidth - clientX - HANDLE_OFFSET
 *
 *   side='right'  handle is on the RIGHT edge of a LEFT-mounted panel.
 *                 Dragging the cursor RIGHTWARD widens the panel.
 *                 Width formula: clientX - HANDLE_OFFSET
 *
 * The component is "dumb": it computes the proposed width and calls
 * `onWidthChange(newWidth)` on every mousemove. The parent applies the
 * clamp (e.g., MIN_WIDTH, MAX_WIDTH = 0.4 * window.innerWidth) and
 * persists the final value on `onDragEnd`.
 *
 * Per bmaps-sidepanel-co-display-and-resize study D7. See
 * criteria/drag-resize-leftward/, criteria/drag-discoverability/, and
 * criteria/smooth-resize-transitions/ for behavioral spec.
 *
 * @param {Object} props
 * @param {'left'|'right'} props.side - which edge of the parent the handle sits on
 * @param {Function} props.onWidthChange - (newWidth) => void; fired on every mousemove
 * @param {Function} [props.onDragStart] - () => void; fired on mousedown
 * @param {Function} [props.onDragEnd] - () => void; fired on mouseup
 * @param {boolean} [props.disabled=false] - when true, the handle ignores mousedown
 * @param {string} [props.iconClass='fa fa-arrows-h'] - Font Awesome icon class
 */
// 2026-06-01: HANDLE_OFFSET = 0 for WYSIWYG ghost-line drag UX. The
// ghost line sits at the cursor's clientX; the user expects the
// panel's new left edge to land exactly there. Setting the offset
// to 0 makes panel_left = clientX on release, regardless of handle
// width. (Geometrically this means the handle's right edge ends up
// at clientX rather than the handle being centered on the cursor —
// a minor visual concession for pixel-perfect drop accuracy. The
// previous values 10 and 4 both produced visible mismatches.)
export const HANDLE_OFFSET = 0;

function defaultViewportWidth() {
    return typeof window !== 'undefined' ? window.innerWidth : 0;
}

export function computeNewWidth(side, clientX, handleOffset = HANDLE_OFFSET, viewportWidth = null) {
    const vw = viewportWidth != null ? viewportWidth : defaultViewportWidth();
    if (side === 'left') {
        return vw - clientX - handleOffset;
    }
    return clientX - handleOffset;
}

export default function SidePanelResizeHandle({
    side = 'left',
    onWidthChange,
    onDragStart,
    onDragEnd,
    disabled = false,
    iconClass = 'fa fa-arrows-h',
}) {
    const { textCss } = useColorSchemeInfo();
    const [resizing, setResizing] = useState(false);
    // 2026-06-04: hover state. Without this, the handle's resting
    // dot opacity (0.45) made it invisible at the column-level mount
    // point, where its #DCF0E6 background matches HelpPanel +
    // WorkflowPane containerStyle. Demo-day feedback ("the ability
    // to change the size of the help/support menu has disappeared")
    // was actually a discoverability regression — the handle was
    // there, but indistinguishable from Help's left chrome. Hover
    // bumps opacity + background tint so the cursor:ew-resize +
    // visible grip together signal "draggable".
    const [hovered, setHovered] = useState(false);
    // 2026-06-01: ghost-line drag pattern (VS Code splitter / Mac Finder
    // column divider). The panel does NOT resize during drag — instead
    // a thin fixed-position line follows the cursor. On mouseup we
    // commit the final width in one shot. This eliminates the per-frame
    // layout/style-recalc cost over a wide DOM that was capping FPS at
    // ~13 even with CSS containment + compositor layer.
    const ghostRef = useRef(null);
    const rafPendingRef = useRef(false);
    const lastClientXRef = useRef(0);

    function handleMouseDown(e) {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
        lastClientXRef.current = e.clientX;
        setResizing(true);
        if (typeof onDragStart === 'function') onDragStart();
        // Build the ghost line: fixed-position, full viewport height,
        // 2px wide, body-attached so it doesn't trigger layout of any
        // sibling or ancestor in the app tree.
        const ghost = document.createElement('div');
        ghost.style.cssText = [
            'position: fixed',
            'top: 0',
            'bottom: 0',
            'width: 2px',
            `left: ${e.clientX - 1}px`,
            // 2026-06-01: #89bd45 = the prod-environment / connected-status
            // green used in ServerInfo.jsx. Matches what users associate
            // with a healthy session.
            'background: rgba(137, 189, 69, 0.9)',
            'box-shadow: 0 0 6px rgba(137, 189, 69, 0.6)',
            'z-index: 9999',
            'pointer-events: none',
            'will-change: left',
        ].join(';');
        document.body.appendChild(ghost);
        ghostRef.current = ghost;
    }

    useEffect(() => {
        if (!resizing) return undefined;

        function flushGhostPosition() {
            rafPendingRef.current = false;
            if (ghostRef.current) {
                ghostRef.current.style.left = `${lastClientXRef.current - 1}px`;
            }
        }

        function onMouseMove(e) {
            e.preventDefault();
            e.stopPropagation();
            lastClientXRef.current = e.clientX;
            // Throttle ghost-position updates to one per animation frame.
            // The DOM mutation is just `style.left` on a fixed-position
            // div, which is compositor-only — no layout, no style recalc
            // on anything else.
            if (!rafPendingRef.current) {
                rafPendingRef.current = true;
                requestAnimationFrame(flushGhostPosition);
            }
        }

        function onMouseUp() {
            // Remove the ghost line.
            if (ghostRef.current) {
                ghostRef.current.remove();
                ghostRef.current = null;
            }
            rafPendingRef.current = false;
            // Commit the final width in one shot.
            const finalWidth = computeNewWidth(side, lastClientXRef.current);
            if (typeof onWidthChange === 'function') onWidthChange(finalWidth);
            setResizing(false);
            if (typeof onDragEnd === 'function') onDragEnd();
        }

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            // Defensive cleanup: if the component unmounts mid-drag,
            // remove the ghost so it doesn't orphan on screen.
            if (ghostRef.current) {
                ghostRef.current.remove();
                ghostRef.current = null;
            }
        };
    }, [resizing, side, onWidthChange, onDragEnd]);

    // 2026-06-01: handle visual redesigned. The previous Font Awesome
    // fa-arrows-h glyph was ~14px intrinsic width sitting in a 12px
    // column, which made it overflow the handle bounds (the source
    // of the "off-center" feedback) AND rendered as an old-fashioned
    // double arrow. Replaced with the universal three-dot grip
    // pattern (macOS / iOS / many web apps): a vertical stack of
    // small dots in the center of the column. Default state is muted
    // gray; on hover it picks up the color-scheme accent; while
    // resizing it tracks the teal ghost line's accent color. Width
    // stays at 8px (slightly narrower than before — the dots are
    // 3px wide, leaving 2.5px padding on each side, perfectly
    // centered with no overflow possible).
    const ACCENT_ACTIVE = 'rgba(137, 189, 69, 0.95)'; // matches ghost line — prod green
    // 2026-06-04: handle background — tints darker on hover so the
    // resting handle is also discoverable, not just the active state.
    // Resting: #DCF0E6 still matches Help/Workflow containerStyle so
    // it reads as panel chrome (intended). Hover: #b8dbc6 is one
    // notch darker, distinguishing the strip from chrome so the user
    // sees there's a draggable thing here.
    const handleBackground = (resizing || hovered) ? '#b8dbc6' : '#DCF0E6';
    // Dot opacity. Resting bumped 0.45 → 0.7 (still subtle but
    // visible against any neighboring panel color). Hover and
    // resizing both go to 1.0 — full strength.
    const dotOpacity = (resizing || hovered) ? 1 : 0.7;
    return (
        // role=separator with aria-orientation=vertical IS the correct ARIA pattern
        // for a focusable resize handle (WAI-ARIA APG split-pane example). The
        // eslint rule classifies separator as non-interactive by default.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize panel"
            tabIndex={disabled ? -1 : 0}
            onMouseDown={handleMouseDown}
            onMouseEnter={() => !disabled && setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            data-testid="sidepanel-resize-handle"
            data-resizing={resizing ? 'true' : 'false'}
            data-hovered={hovered ? 'true' : 'false'}
            data-side={side}
            data-disabled={disabled ? 'true' : 'false'}
            style={{
                cursor: disabled ? 'default' : 'ew-resize',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: '8px',
                minWidth: '8px',
                userSelect: 'none',
                opacity: disabled ? 0.3 : 1,
                position: 'relative',
                // 2026-06-01: solid background so the handle reads as
                // panel chrome rather than letting the dark 3Dmol
                // workspace show through behind the dots. Matches the
                // adjacent panel container (Help + Workflow both use
                // #DCF0E6 light green via containerStyle).
                // 2026-06-04: derived `handleBackground` so hover state
                // tints darker for discoverability without breaking the
                // matches-chrome-by-default contract.
                background: handleBackground,
                transition: 'background 120ms ease-out',
            }}
        >
            {/* Three-dot grip. The dots inherit the panel's text color
                in default state (a muted shade against the panel
                background) and switch to the ghost-line accent during
                an active drag for a coherent visual story. */}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    pointerEvents: 'none',
                    transition: 'opacity 120ms ease-out',
                    opacity: dotOpacity,
                }}
            >
                {[0, 1, 2].map((i) => (
                    <span
                        key={i}
                        style={{
                            display: 'block',
                            width: '3px',
                            height: '3px',
                            borderRadius: '50%',
                            // 2026-06-01: explicit dark gray instead of
                            // textCss. textCss inherits the colorscheme's
                            // text color (white on the default 'black'
                            // colorscheme) which was invisible against the
                            // new light-green handle background.
                            background: resizing ? ACCENT_ACTIVE : '#5d5d5d',
                        }}
                    />
                ))}
            </div>
        </div>
    );
}
